import { ChangeDetectorRef, Component, HostBinding } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { FixtureType } from '@cds/betting-offer';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { translationToStr } from '@frontend/sports/common/core/utils/translations';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { WinGameFixture } from '@frontend/sports/racing/feature/models';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { Subject, takeUntil } from 'rxjs';

import { Promotion } from '../../../crm-offer-data/crm-offer.model';
import { CrmPromotionProviderService } from '../../../crm-promotion/crm-promotion-provider.service';
import { TrackNavArea } from '../../../navigation/navigation-tracking.model';
import { FixtureUpdateService } from '../../common/fixture-update.service';
import { CrmPromotionMapperService } from '../../services/crm-promotion-mapper.service';
import { AntepostFixtureModel } from './antepost-fixture.resolver';

interface AntepostFixtureComponentViewModel {
    fixture: WinGameFixture;
    fixtureTitle: string;
    isGolf: boolean;
}

@HooksWireup()
@Component({
    selector: 'ms-antepost-fixture',
    templateUrl: 'antepost-fixture.html',
    standalone: false,
})
export class AntepostFixtureComponent extends OnRouteResolve<AntepostFixtureModel> {
    private static emptyViewModel: AntepostFixtureComponentViewModel = {} as AntepostFixtureComponentViewModel;
    vm: AntepostFixtureComponentViewModel = AntepostFixtureComponent.emptyViewModel;
    crmBannerPromotions: Promotion[] = [];
    private modelFixtureChanged = new Subject<void>();
    sportConstant = SportConstant;

    @HostBinding('class.racing-antepost-fixture') get racingAntepostFixture(): boolean {
        return this.vm && !this.vm.isGolf;
    }

    constructor(
        public sitecore: Sitecore,
        private crmPromotionProvider: CrmPromotionProviderService,
        private crmPromotionMapper: CrmPromotionMapperService,
        route: ActivatedRoute,
        private redirectHelper: RedirectHelperService,
        private trackingService: TrackingService,
        protected fixtureUpdateService: FixtureUpdateService,
        private changeDetection: ChangeDetectorRef,
    ) {
        super(route);
    }

    onRouteResolved(model: AntepostFixtureModel): void {
        if (model.fixture) {
            this.setViewModel(model.fixture);
        } else {
            this.vm = AntepostFixtureComponent.emptyViewModel;
        }
        this.subscribeToFixtures(this.vm.fixture);
        this.crmPromotionProvider.promotions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((promos) => this.crmPromotionChanged(promos));
    }

    private crmPromotionChanged(promos: Promotion[]): void {
        this.crmBannerPromotions = this.crmPromotionMapper.getBannerPromotions(this.vm.fixture, promos);
    }

    private setViewModel(fixture: WinGameFixture): void {
        const isGolf = fixture.sport.id === SportConstant.Golf;
        let fixtureTitle = '';
        if (fixture.fixtureType === FixtureType.Antepost && isGolf) {
            fixtureTitle = `${translationToStr(fixture.fixtureGroup.name)} - ${translationToStr(fixture.name)}`;
        } else {
            fixtureTitle = translationToStr(fixture.name);
        }

        this.vm = {
            fixtureTitle,
            fixture,
            isGolf,
        };

        this.crmBannerPromotions = this.crmPromotionMapper.getBannerPromotions(this.vm.fixture, this.crmBannerPromotions);
    }

    goBack = (): void => {
        this.trackingService.track(trackingConstants.EVENT_NAVIGATION_MENUS, {
            [trackingConstants.PAGE_NAVIGATION_MENUS]: `${TrackNavArea.NavBreadcrumb}_Back`,
        });
        this.redirectHelper.goBack();
    };

    private subscribeToFixtures(f: WinGameFixture) {
        this.fixtureUpdateService
            .handleFixtureUpdates(f)
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.modelFixtureChanged))
            .subscribe((updatedFixture) => {
                this.vm.fixture = updatedFixture;
                this.changeDetection.markForCheck();
            });
    }
}
